import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://metamorphobiz.com';
    const schemaId = 'schema-privacy';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Privacy Policy | MetamorphoBiz Inc.',
        description: 'MetamorphoBiz Inc. Privacy Policy — learn how we collect, use, and protect your personal information. Houston, Texas.',
        url: `${siteUrl}/privacy`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: siteUrl },
        publisher: {
          '@type': 'Organization',
          name: 'MetamorphoBiz Inc.',
          url: siteUrl,
        },
        dateModified: '2026-03-08',
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/8cd6047605fa8bd9b83f93f5c3fc7ad9/47c456da8a70802ef8f75066d63b6e27.png" 
                alt="MetamorphoBiz Logo" 
                className="h-12 w-auto"
              />
            </Link>
            
            <Link
              to="/"
              className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center space-y-4">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900">
              Privacy Policy
            </h1>
            <p className="text-lg text-slate-600">
              MetamorphoBiz Inc.
            </p>
            <p className="text-base text-slate-500">
              Last Updated: March 8, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-slate-700 leading-relaxed">
                MetamorphoBiz Inc. ("MetamorphoBiz," "we," "our," or "us") respects your privacy and is committed to protecting the information you share with us. This Privacy Policy explains what information we collect, how we use it, and whether we share it with third parties.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mt-4">
                By using our website or communicating with us, you agree to the practices described in this policy.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Information We Collect
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-6">
                We may collect the following types of information when you interact with our website, services, or communications.
              </p>

              <div className="bg-slate-50 rounded-2xl p-8 mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Personal Information</h3>
                <p className="text-base text-slate-700 mb-4">This may include:</p>
                <ul className="space-y-2">
                  {[
                    'Name',
                    'Email address',
                    'Phone number',
                    'Company or organization name',
                    'Any information you voluntarily provide when contacting us'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                      <span className="text-base text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Technical Information</h3>
                <p className="text-base text-slate-700 mb-4">When you visit our website, we may collect:</p>
                <ul className="space-y-2 mb-4">
                  {[
                    'IP address',
                    'Browser type',
                    'Device information',
                    'Pages visited on our website',
                    'Date and time of visits'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                      <span className="text-base text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base text-slate-700">
                  This information helps us improve our website and services.
                </p>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                How We Use Your Information
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'Respond to inquiries and provide customer support',
                  'Communicate with clients and potential clients',
                  'Provide information about our services',
                  'Improve our website and services',
                  'Maintain the security of our systems'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-teal-600 text-xl mt-0.5"></i>
                    <span className="text-base text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base text-slate-700 leading-relaxed">
                We may also use your information to send service-related communications.
              </p>
            </div>

            {/* Text Messaging */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Text Messaging and Communications
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                If you choose to contact us via phone or opt in to receive messages, we may send text messages for:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'responding to inquiries',
                  'customer support',
                  'service-related updates',
                  'informational communications about our services'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-teal-600 text-xl mt-0.5"></i>
                    <span className="text-base text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-teal-50 border-l-4 border-teal-600 rounded-lg p-6 space-y-2">
                <p className="text-base text-slate-700"><strong>Message frequency varies.</strong></p>
                <p className="text-base text-slate-700">Standard message and data rates may apply.</p>
                <p className="text-base text-slate-700">
                  You may opt out of receiving text messages at any time by replying <strong>STOP</strong>.
                </p>
              </div>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Information Sharing
              </h2>
              <div className="bg-slate-900 text-white rounded-2xl p-8 mb-6">
                <p className="text-lg font-semibold">
                  MetamorphoBiz <strong>does not sell, rent, or trade your personal information</strong>.
                </p>
              </div>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                We may share information only in the following situations:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'with service providers that help us operate our business (such as website hosting or communication platforms)',
                  'if required by law or legal process',
                  'to protect the rights and security of our business'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-teal-600 text-xl mt-0.5"></i>
                    <span className="text-base text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base text-slate-700 leading-relaxed">
                We only share information when necessary to provide our services.
              </p>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Data Security
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                We take reasonable steps to protect the information we collect from unauthorized access, misuse, or disclosure.
              </p>
              <p className="text-base text-slate-700 leading-relaxed">
                However, no internet transmission or storage system can be guaranteed to be completely secure.
              </p>
            </div>

            {/* Your Privacy Choices */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Your Privacy Choices
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-4">
                You may choose to:
              </p>
              <ul className="space-y-3 mb-4">
                {[
                  'opt out of marketing communications',
                  'request that we update or delete your information',
                  'stop receiving text messages by replying STOP'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-teal-600 text-xl mt-0.5"></i>
                    <span className="text-base text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base text-slate-700 leading-relaxed">
                For assistance, contact us using the information below.
              </p>
            </div>

            {/* Third-Party Websites */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Third-Party Websites
              </h2>
              <p className="text-base text-slate-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of those websites.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Changes to This Privacy Policy
              </h2>
              <p className="text-base text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Any updates will be posted on this page with a revised effective date.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-teal-600 rounded-full"></div>
                Contact Information
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-6">
                If you have questions about this Privacy Policy or how we handle your information, please contact us:
              </p>
              <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">MetamorphoBiz Inc.</h3>
                    <p className="text-base text-slate-600">Houston, Texas</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@metamorphobiz.com" className="text-base text-teal-600 hover:text-teal-700">
                      contact@metamorphobiz.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Website</h3>
                    <a href="https://metamorphobiz.com" className="text-base text-teal-600 hover:text-teal-700">
                      https://metamorphobiz.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {scrolled && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 bg-teal-600 text-white rounded-full shadow-2xl hover:bg-teal-700 transition-all hover:scale-110 cursor-pointer z-40 flex items-center justify-center"
        >
          <i className="ri-arrow-up-line text-2xl"></i>
        </button>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 text-white">
        <div className="border-t border-white/10"></div>
        
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">
              © 2024 MetamorphoBiz. All rights reserved.
            </p>
            <div className="flex gap-8">
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Privacy Policy
              </Link>
              <button className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Terms of Service
              </button>
              <button className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}